import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp, FileCopy, Receipt } from '@material-ui/icons';
import * as Styled from './styled';
import { parseCode } from 'util/utils';
import {
    setMatter,
    setNarrative,
    setWorkLocale,
    setPhase,
    setTask,
    setAct,
    setFFTask,
    setFFAct,
    setActionCode,
    setCodeSetTemplate,
    changeNarrativeLanguage,
    fetchPhaseCodes,
    fetchTaskCodes,
    fetchActivityCodes,
    fetchFFTaskCodes,
    fetchFFActCodes,
    fetchMatters,
    getMatterTooltipText,
    getClientText,
    getWorkLocaleValue,
    setIncidentNumber,
    setAiUsageCount,
    setNoChangeRequiredCount,
    shouldSuppressFFFields,
    fetchFFActionCodesWithDefaultOnTop
} from 'util/timeEntry';
import RootAPI from 'api/interfaces/RootAPI';
import TimeEntry from 'api/immutables/ImmutableTimeEntry';
import { ValidationState } from 'api/immutables/validators';
import {
    ActionCode,
    Code,
    CodeSetTemplate,
    Features,
    Matter,
    NARRATIVE_VALIDATION_MESSAGES,
    TimeKeeperAssignment,
    WorkLocale
} from 'api/types/types';
import { RootStoreContext } from '../../App';
import NarrativeField from 'components/NarrativeField';
import AutoCompleteField from 'components/AutoCompleteField/AutoCompleteField';
import { MatterItemFormatter } from 'components/TemplateForm/TemplateForm';
import { LinearProgressBar } from 'components/LoadingSpinner/LinearProgressBar';
import { ApiConsumer } from 'common/ApiProvider';
import { TKConsumer } from 'common/TKProvider';
import { FlexDiv } from 'common/flex';
import { FeaturesConsumer } from 'common/FeaturesProvider';
import Template from 'api/immutables/ImmutableTemplate';
import { getTemplateTooltipText } from 'util/template';
import BillingRulesDialog from 'components/TimeEntryForm/BillingRulesDialog';

interface Props {
    timeEntry: TimeEntry;
    onChange: (t: TimeEntry, newVState?: ValidationState) => void;
    setTempWorkLocaleId: (id: number) => void;
    disabled?: boolean;
    minHeight?: number;
    validationState?: ValidationState;
    minNarrativeLength: number;
    maxNarrativeLength: number;
    actionCodesRequired: boolean;
    onSetFieldLoader?: (value: boolean) => void;
    expand: boolean;
    onExpand: (expanded: boolean) => void;
    isNewGroup: boolean;
}

function GridTimeEntry(props: Props) {
    const {
        timeEntry,
        disabled,
        expand,
        validationState,
        minHeight,
        actionCodesRequired,
        onChange,
        onExpand,
        setTempWorkLocaleId
    } = props;
    const rootStore = useContext(RootStoreContext);
    const gridViewStore = rootStore.gridViewStore;
    const { t } = useTranslation(['timeentries']);
    const matterRef = useRef<HTMLDivElement>(null);
    const billingRef = useRef<HTMLButtonElement>(null);
    const [fieldLoader, setFieldLoader] = useState(false);
    const [expanded, setExpanded] = useState(expand);
    const [showTemplateField, setShowTemplateField] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<Template | undefined>(undefined);
    const [narrativeText, setNarrativeText] = useState<string | null>(timeEntry.narrative);

    useEffect(() => {
        if (props.isNewGroup) {
            let elem = ReactDOM.findDOMNode(matterRef.current) as HTMLElement;
            elem.getElementsByTagName('input')[0].focus();
        }
    }, []);

    useEffect(() => {
        setExpanded(expand);
    }, [expand]);

    useEffect(() => {
        if (showTemplateField && !selectedTemplate) {
            if (matterRef && matterRef.current) {
                const input = matterRef.current.querySelector('input');
                if (input) {
                    input.focus();
                }
            }
        }
    }, [showTemplateField, selectedTemplate]);

    const isProject = rootStore.appStore.isProject;
    const matterLabel = rootStore.appStore.features.EpochConfigMatterLabel;
    const defaultFFActionCode = rootStore.appStore.features.EpochConfigDefaultFFActionCode;
    const client = getClientText(timeEntry);
    let conflictingBannedWords = [] as string[];
    let conflictingBlockBillingWords = [] as string[];
    let matterStatusErrText: string;
    if (validationState) {
        conflictingBannedWords = validationState.narrativeBannedWords;
        conflictingBlockBillingWords = validationState.narrativeBlockBillingWords;
    }
    let bwText = (conflictingBannedWords.length > 0) ?
    `${conflictingBannedWords.join(', ')}` : '';
    let bbText = (conflictingBlockBillingWords.length > 0)
    ? `${conflictingBlockBillingWords.join(', ')}` : '';
    let narBwBbWords = bwText ? `${bwText}` + (bbText && ', ') + `${bbText}` : `${bbText}`
    let narHelperText = (bwText || bbText) &&
        t(NARRATIVE_VALIDATION_MESSAGES.BANNED_BLOCKING_WORDS, { narBwBbWords });
    let narrativeErrored = (validationState && validationState.narrativeLength) ||
    (validationState && validationState.maxNarrativeLength) ||
    ((conflictingBannedWords.length + conflictingBlockBillingWords.length) > 0);
    if (validationState && validationState.narrativeLength) {
        const minLength = validationState.narrativeMinLength;
        narHelperText = t(NARRATIVE_VALIDATION_MESSAGES.MIN_LENGTH, { minLength });
    }
    if (validationState && validationState.matterStatusInvalid) {
        matterStatusErrText = `${t('form.validation.matter.status', { matterLabel })}: ${timeEntry.matterStatusDesc}`;
    }
    if (validationState && validationState.matterEntryTypeInvalid) {
        matterStatusErrText = t('form.validation.matter.type_cost', { matterLabel });
    }
    if (validationState && validationState.maxNarrativeLength) {
        const maxLength = validationState.narrativeMaxLength;
        narHelperText = t(NARRATIVE_VALIDATION_MESSAGES.MAX_LENGTH, { maxLength });
    }

    const _setMatter = async (m: Matter | null) => {
        setFieldLoader(true);
        setExpanded(true);
        await setMatter(m, timeEntry, onChange, validationState, true);
        setFieldLoader(false);
        onExpand(false);
    }

    const _setWorkLocale = async (w: WorkLocale | null) => {
        await setWorkLocale(w, timeEntry, onChange, validationState);
        if (w) {
            setTempWorkLocaleId(w.id);
        }
    }

    const handleExpand = () => {
        onExpand(expanded);
        setExpanded(!expanded);
    }

    const handleBillableChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        let entry = timeEntry.setBillable(event.target.value === 'true');
        onChange(entry);
    }
    
    const setTemplateForGrid = async (template: Template | undefined) => {
        setSelectedTemplate(template);
        if (template) {
            setFieldLoader(true);
            const updatedTimeEntry = await gridViewStore.setTemplateForEntry(
                timeEntry.clone(),
                template,
                narrativeText
            );
            onChange(updatedTimeEntry);
            setShowTemplateField(false);
            setExpanded(true);
            setFieldLoader(false);
            if (billingRef && billingRef.current) {
                billingRef.current.focus();
                billingRef.current.blur();
            }
        }
    }   

    const toggleTemplateField = () => {
        const isSwitchingToTemplate = !showTemplateField;
        setShowTemplateField(isSwitchingToTemplate);
        setTimeout(() => {
            if (matterRef.current) {
                const input = matterRef.current.querySelector('input');
                if (input && ((isSwitchingToTemplate && !selectedTemplate) || (!isSwitchingToTemplate && !timeEntry.matter))) {
                    input.focus();
                    input.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown' }));
                }
            }
        }, 0);
    }

    const toggleBillingRules = () => {
        const newShowBillingRules = !timeEntry.showBillingRules;
        const entry = timeEntry.setShowBillingRules(newShowBillingRules);
        onChange(entry)
    }

    return (
        <FeaturesConsumer>
            { (features: Features) =>
            <ApiConsumer>
                { (api: RootAPI) =>
            <TKConsumer>
                { (tk: TimeKeeperAssignment) =>
                    <FlexDiv direction="column">
                        <Styled.FieldContainer minHeight={minHeight}>
                            <Styled.FieldsMain>
                                {features.EpochConfigTimeEntriesMattersRequired &&
                                    <Styled.MatterContainer draft={!disabled} showTemplate={showTemplateField}>
                                        <div ref={matterRef}>
                                            {showTemplateField ? (
                                                <AutoCompleteField
                                                    label={t('field.template.title')}
                                                    fetch={(...params) => rootStore.templateStore.fetchTemplatesInAutoComplate(...params, timeEntry)}
                                                    currentItem={selectedTemplate ? selectedTemplate : null}
                                                    clearable={true}
                                                    onClear={() => setTemplateForGrid(undefined)}
                                                    getItemText={(m: Template) => `${m.name}`}
                                                    onSelect={(template) => setTemplateForGrid(template as Template | undefined)}
                                                    tooltip={(m: Template) => getTemplateTooltipText(m, t)}
                                                    disabled={false || !tk.writable}
                                                />
                                            ) : (
                                                <AutoCompleteField
                                                    label={t('field.matter', { matterLabel })}
                                                    errorMessage={t('form.validation.matter.invalid', { matterLabel })}
                                                    errored={expanded && validationState ? validationState.missing.matter : false}
                                                    fetch={(...params) => fetchMatters(timeEntry, ...params)}
                                                    currentItem={timeEntry.matter}
                                                    clearable={true}
                                                    onClear={() => _setMatter(null)}
                                                    formatItem={MatterItemFormatter}
                                                    getItemText={(m: Matter) => parseCode(m.name, m.number)}
                                                    onSelect={_setMatter}
                                                    tooltip={getMatterTooltipText}
                                                    disabled={disabled}
                                                />
                                            )}
                                            {fieldLoader && <LinearProgressBar color={'primary'} progressBar={50}/>}
                                        </div>
                                        {!(disabled || showTemplateField) &&
                                            <Tooltip title={t('form.icon.billing_rules.tooltip')}>
                                                <Styled.BillingRulesIcon
                                                    onClick={toggleBillingRules}
                                                    disabled={!timeEntry.matterId || fieldLoader}
                                                    buttonRef={billingRef}
                                                >
                                                    <Receipt fontSize="small"/>
                                                </Styled.BillingRulesIcon>
                                            </Tooltip>
                                        }
                                        {!disabled &&
                                            <Tooltip
                                                title={t(
                                                    `view.grid.item.action.template.tooltip.${showTemplateField ? 'matter' : 'template'}`,
                                                    { ns: 'home', matterLabel })
                                                }
                                            >
                                                <IconButton
                                                    onClick={toggleTemplateField}
                                                    color={showTemplateField ? 'primary' : undefined}
                                                    disabled={fieldLoader}
                                                >
                                                    <FileCopy />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    </Styled.MatterContainer>
                                }
                                <Tooltip title={timeEntry.narrative}>
                                    <Styled.NarrativeFieldContainer
                                        aria-label={timeEntry.billingLangText ?
                                            `${t('form.field.narrative.label')} (${timeEntry.billingLangText})`
                                            :
                                            t('form.field.narrative.label')}
                                    >
                                        <NarrativeField
                                            showCharCount={false}
                                            style={{
                                                maxHeight: '48px',
                                                overflow: 'auto'
                                            }}
                                            helperText={expanded && narHelperText.length > 0 ? narHelperText : undefined}
                                            error={expanded && narrativeErrored}
                                            value={timeEntry.narrative}
                                            placeholder={timeEntry.billingLangText ?
                                                `${t('form.field.narrative.label')} (${timeEntry.billingLangText})`
                                                :
                                                t('form.field.narrative.label')
                                            }
                                            onChange={(text: string, acceptedAi: boolean) => {
                                                setNarrative(text, timeEntry, onChange, validationState, acceptedAi);
                                                setNarrativeText(text);
                                            }}
                                            maxLength={features.EpochConfigNarrativesMaximumChars}
                                            dictionaryKey={timeEntry.matterBillingLangDictionary}
                                            changeLanguage={(key: string) => changeNarrativeLanguage(key, timeEntry, onChange)}
                                            disabled={disabled}
                                            enableAI={true}
                                            timeEntry={timeEntry}
                                            isGridView={true}
                                            updateAiUsageCount={() => setAiUsageCount(timeEntry, onChange, validationState)}
                                            updateNoChangeRequiredCount={() => setNoChangeRequiredCount(timeEntry, onChange)}
                                            aiUsageCount={timeEntry.aiUsageCount}
                                        />
                                    </Styled.NarrativeFieldContainer>
                                </Tooltip>
                                <Tooltip title={t(`${expanded ? 'collapse' : 'expand'}`, { ns: 'common' })}>
                                    <IconButton onClick={handleExpand} style={{ height: '48px' }}>
                                        {expanded ? <KeyboardArrowUp/> : <KeyboardArrowDown />}
                                    </IconButton>
                                </Tooltip>
                            </Styled.FieldsMain>
                            {expanded && <Styled.Fields>
                                <TextField
                                    value={client}
                                    title={client}
                                    label={t('field.client')}
                                    disabled={true}
                                />
                                {features.EpochConfigWorkLocaleEnabled &&
                                    <AutoCompleteField
                                        label={t('form.field.work_location.label')}
                                        fetch={(search: string) => api.TimeEntry.searchWorkLocales(search)}
                                        currentItem={timeEntry.workLocaleId ? getWorkLocaleValue(timeEntry) : null}
                                        getItemText={(w: WorkLocale) => parseCode(w.localeSearch, null)}
                                        clearable={true}
                                        onClear={() => _setWorkLocale(null)}
                                        onSelect={_setWorkLocale}
                                        disabled={disabled || !tk.writable}
                                        errorMessage={t('form.validation.work_location')}
                                        errored={validationState ? validationState.missing.workLocale : false}
                                    />
                                }
                                {(timeEntry.matter && !timeEntry.isPosted()) &&
                                    features.EpochConfigCodeSetTemplatesEnabled &&
                                    <AutoCompleteField
                                        label={t('form.field.code_template.label')}
                                        fetch={(...params) => api.CodeSet.getCodeSets(timeEntry.matterId!, ...params)}
                                        currentItem={timeEntry.selectedCodeSetTemplate}
                                        clearable={true}
                                        disabled={timeEntry.isPosted() || disabled || !tk.writable}
                                        onClear={() => setCodeSetTemplate(null, timeEntry, onChange, validationState)}
                                        getItemText={(cs: CodeSetTemplate) => parseCode(cs.codeSetName, cs.codeSetRef)}
                                        onSelect={(c: CodeSetTemplate) => setCodeSetTemplate(c, timeEntry, onChange, validationState)}
                                    />
                                }
                                {/** TODO figure out when to display phase and tasks sets */}
                                {<>
                                    {timeEntry.isPhaseCode && <>
                                        <AutoCompleteField
                                            errorMessage={t('form.validation.phase')}
                                            errored={validationState ? validationState.missing.phase : false}
                                            label={t('form.field.phase.label')}
                                            currentItem={timeEntry.phase}
                                            clearable={true}
                                            disabled={timeEntry.matterId === null || disabled || timeEntry.isPosted() || !tk.writable}
                                            onClear={() => setPhase(null, timeEntry, onChange, validationState)}
                                            onSelect={(c: Code) => setPhase(c, timeEntry, onChange, validationState)}
                                            fetch={(searchText) => fetchPhaseCodes(timeEntry, searchText)}
                                            getItemText={(c: Code) => parseCode(c.name, c.description)}
                                        />
                                        <AutoCompleteField
                                            errorMessage={t('form.validation.task')}
                                            errored={validationState ? validationState.missing.task : false}
                                            label={t('form.field.task.label')}
                                            currentItem={timeEntry.task}
                                            clearable={true}
                                            onSelect={(c: Code) => setTask(c, timeEntry, onChange, validationState)}
                                            onClear={() => setTask(null, timeEntry, onChange, validationState)}
                                            fetch={(searchText) => fetchTaskCodes(timeEntry, searchText)}
                                            disabled={timeEntry.phase === null || disabled || timeEntry.isPosted() || !tk.writable}
                                            getItemText={(c: Code) => parseCode(c.name, c.description)}
                                        />
                                        {timeEntry.taskCodeTicketRequired && (
                                            <TextField
                                                label={t('form.field.incident_number.label')}
                                                fullWidth={true}
                                                value={timeEntry.reference || ''}
                                                inputProps={{
                                                    maxLength: 20,
                                                    'aria-label': t('form.field.incident_number.label')
                                                }}
                                                onChange={(e) => setIncidentNumber(e.target.value, timeEntry, onChange, validationState)}
                                                error={validationState ? validationState.missing.incidentNumber : false}
                                                disabled={disabled || !tk.writable}
                                                helperText={(validationState && validationState.missing.incidentNumber) ? t('form.validation.incident_number') : ''}
                                            />
                                        )}
                                        {isProject &&
                                            <FormControl
                                                fullWidth={true}
                                                disabled={disabled || timeEntry.isPosted() || !tk.writable}
                                            >
                                                <InputLabel shrink={typeof timeEntry.billable === 'boolean'}>{t('form.field.billable.label')}</InputLabel>
                                                <Select
                                                    displayEmpty={true}
                                                    name={'select-billable'}
                                                    value={timeEntry.billable !== null ? timeEntry.billable : undefined}
                                                    onChange={handleBillableChange}
                                                    SelectDisplayProps={{
                                                        'aria-label': `
                                                            ${t('form.field.billable.label')}:
                                                            ${timeEntry.billable !== null ? t(`form.field.billable.option.${timeEntry.billable ? 'yes' : 'no'}`) : ''}
                                                        `
                                                    }}
                                                >
                                                    <MenuItem value={'false'}>{t('form.field.billable.option.no')}</MenuItem>
                                                    <MenuItem value={'true'}>{t('form.field.billable.option.yes')}</MenuItem>
                                                </Select>
                                            </FormControl>
                                        }
                                    </>}
                                    {timeEntry.isActCode &&
                                    <AutoCompleteField
                                        errorMessage={t('form.validation.activity')}
                                        errored={validationState ? validationState.missing.activity : false}
                                        label={t('form.field.activity.label')}
                                        currentItem={timeEntry.activity}
                                        clearable={true}
                                        onClear={() => setAct(null, timeEntry, onChange, validationState)}
                                        onSelect={(c: Code) => setAct(c, timeEntry, onChange, validationState)}
                                        fetch={(searchText) => fetchActivityCodes(timeEntry, searchText)}
                                        disabled={!timeEntry.matterId || disabled || timeEntry.isPosted() || !tk.writable}
                                        getItemText={(c: Code) => parseCode(c.name, c.description)}
                                    />
                                    }
                                </>}
                                {(actionCodesRequired || defaultFFActionCode) &&
                                    <AutoCompleteField
                                        errorMessage={t('form.validation.action')}
                                        errored={validationState ? validationState.missing.action : false}
                                        label={t('form.field.action_code.label')}
                                        currentItem={timeEntry.actionCodeObj}
                                        disabled={disabled || timeEntry.isPosted() || !tk.writable}
                                        clearable={true}
                                        onClear={() => setActionCode(null, timeEntry, onChange, validationState)}
                                        onSelect={(c: ActionCode) => {
                                            setActionCode(c, timeEntry, onChange, validationState);
                                            setNarrativeText(c.actionText);
                                        }}
                                        fetch={(searchText) =>
                                            defaultFFActionCode
                                                ? fetchFFActionCodesWithDefaultOnTop(searchText)
                                                : api.Code.getActionCodes(timeEntry.matterId!, undefined, searchText)
                                        }
                                        getItemText={(code: ActionCode) =>
                                                `${code.actionCode}${code.actionText ? `
                                                    - ${code.actionText}` : ''}`}
                                    />
                                }
                                {
                                    features.EpochConfigFlatFeeCodesEnabled && timeEntry.isFfTaskCode && !shouldSuppressFFFields(timeEntry) &&
                                    <>
                                        <AutoCompleteField
                                            errorMessage={t('form.validation.ff_task')}
                                            errored={validationState ? validationState.missing.ffTask : false}
                                            label={t('form.field.ff_task.label')}
                                            currentItem={timeEntry.ffTask}
                                            clearable={true}
                                            onClear={() => setFFTask(null, timeEntry, onChange, validationState)}
                                            onSelect={(c: Code) => setFFTask(c, timeEntry, onChange, validationState)}
                                            fetch={(searchText) => fetchFFTaskCodes(timeEntry, searchText)}
                                            getItemText={(c: Code) => parseCode(c.name, c.description)}
                                            disabled={disabled || timeEntry.isPosted() || !tk.writable}
                                        />
                                        <AutoCompleteField
                                            errorMessage={t('form.validation.ff_activity')}
                                            errored={validationState ? validationState.missing.ffAct : false}
                                            label={t('form.field.ff_activity.label')}
                                            currentItem={timeEntry.ffActivity}
                                            disabled={!timeEntry.ffTaskCodeId || disabled || timeEntry.isPosted() || !tk.writable}
                                            clearable={true}
                                            onClear={() => setFFAct(null, timeEntry, onChange, validationState)}
                                            onSelect={(c: Code) => setFFAct(c, timeEntry, onChange, validationState)}
                                            fetch={(searchText) => fetchFFActCodes(timeEntry, searchText)}
                                            getItemText={(c: Code) => parseCode(c.name, c.description)}
                                        />
                                    </>}
                            </Styled.Fields>}
                        </Styled.FieldContainer>
                        <FlexDiv flex={1} />
                        <Styled.FieldContainer
                            minHeight={minHeight}
                            style={{display: 'grid', alignItems: 'end'}}
                        >
                            {timeEntry.actionResponse &&
                                <FormHelperText error={true}>
                                    {timeEntry.actionResponse}
                                </FormHelperText>
                            }
                            {matterStatusErrText &&
                                <FormHelperText error={true}>
                                    {matterStatusErrText}
                                </FormHelperText>
                            }
                        </Styled.FieldContainer>
                        <BillingRulesDialog timeEntry={timeEntry} close={toggleBillingRules}/>
                    </FlexDiv>}
            </TKConsumer>}
                </ApiConsumer>
            }
        </FeaturesConsumer>
    );
}

export default GridTimeEntry;